import { Link } from "react-router-dom";
import { ArrowRight, Clock, Handshake, MapPin } from "lucide-react";
import Seo from "@/components/Seo";
import { PersoenlichBlock, ProgrammeUndTransfer, BranchenLinksOwl } from "@/components/HerfordPositionierung";
import { FadeUp } from "@/components/Reveal";
import ContactSection from "@/components/ContactSection";
import { IndividualBox } from "@/components/IndividualBox";
import ReferenceGallery from "@/components/ReferenceGallery";
import { ShowcaseReel } from "@/components/ShowcaseReel";
import { Note } from "@/components/Campaign";
import { TrustLine } from "@/components/Formula";
import { Fragen } from "@/components/Unterseite";

const REF_CATEGORY = { video: "film", audio: "audio", agentur: "agentur", filmproduktion: "film", webdesign: "agentur" };

/* Responsive Varianten (768/1280/Original) – Dateien liegen unter public/assets. */
const HERO_WIDTHS = { "video-1920": [768, 1280, 1920], "audio-1920": [768, 1280, 1920], "agentur-b-1280": [768, 1280], "video-b-1600": [768, 1280, 1600], "agentur-1920": [768, 1280, 1920] };
const heroSrcSet = (img) => {
  const m = img.match(/\/assets\/(.+)-(\d+)\.webp$/);
  if (!m) return undefined;
  const widths = HERO_WIDTHS[`${m[1]}-${m[2]}`];
  return widths ? widths.map((w) => `/assets/${m[1]}-${w}.webp ${w}w`).join(", ") : undefined;
};

// Kampagnen-Look: Tier, Claim und Pills je Dienst (Bild-Hero wird ersetzt)
const CAMPAIGN = {
  video: { animal: "/img/kampagne/adler.webp", alt: "Adler mit Sonnenbrille und Filmkamera", label: "Film · Seit 2000", h1: ["Dein Film", "sagt mehr", "als 1000 Klicks."], sub: "Imagefilm, Recruiting, Werbespot – aus Herford für ganz OWL.", pills: ["Imagefilm", "Recruitingfilm", "Werbespot", "Drohne", "Event & Content"], cta: { label: "Filmprojekt anfragen", to: "/kontakt" } },
  filmproduktion: { animal: "/img/kampagne/adler.webp", alt: "Adler mit Sonnenbrille und Filmkamera", label: "Filmproduktion Herford", h1: ["Dein Film", "sagt mehr", "als 1000 Klicks."], sub: "Von der Idee bis zum fertigen Schnitt – ein Team, ein Ansprechpartner, ein Termin.", pills: ["Konzept & Storyboard", "Dreh & Drohne", "Schnitt & Grading", "Ton & Sprecher"], cta: { label: "Filmprojekt anfragen", to: "/kontakt" } },
  audio: { animal: "/img/kampagne/baer.webp", alt: "Bär mit Sonnenbrille und Kopfhörern am Mikrofon", label: "Audio · VOICE.NRW", h1: ["Gefühl geht", "zuerst ins Ohr.", "Bei uns stimmt jeder Ton."], sub: "Sprachaufnahmen, Podcasts, Telefonansagen, Radiowerbung und Sounddesign – aus unserem Tonstudio VOICE.NRW in Herford.", pills: ["Sprecher & Voice-over", "Podcast-Produktion", "Radiowerbung", "Telefonansagen", "Sounddesign"], cta: { label: "Zum Tonstudio VOICE.NRW", href: "https://voicenrw.de/" } },
  agentur: { animal: "/img/kampagne/krokodil.webp", alt: "Krokodil mit Sonnenbrille und Smartphone", label: "Agentur · Kreativ seit 2000", h1: ["Bissig", "gedacht.", "Sauber gemacht."], sub: "Konzept, Kampagne, Grafik, Layout, Text und Web – alles aus einer Hand, ohne Agentur-Blabla.", pills: ["Konzept & Kampagne", "Grafik & Layout", "Corporate Design", "Text & Content", "Web & Social"], cta: { label: "Projekt besprechen", to: "/kontakt" } },
  webdesign: { animal: "/img/kampagne/computer.webp", alt: "Alter Computer mit Sonnenbrille", label: "Webdesign Herford", h1: ["Deine Website", "ist alt.", "Dein Unternehmen nicht."], sub: "Neu gedacht, klar gestaltet, einfach online – mit Homepage in 6 Schritten.", pills: ["Homepage in 6 Schritten", "WordPress-Hosting", "Fairer Monatspreis"], cta: { label: "Kostenlose Vorschau", to: "/homepage" } },
};

const SERVICES = {
  video: {
    number: "01",
    label: "Film",
    h1a: "Filmproduktion &",
    h1b: "Videoproduktion aus Herford",
    title: "Filmproduktion & Videoproduktion Herford | DIMEDIAL Studio",
    description: "Filmproduktion und Videoproduktion aus Herford für ganz OWL: Imagefilm, Werbefilm, Recruitingfilm, Contentproduktion, Drohnenaufnahmen und FPV. DIMEDIAL – seit 2000.",
    img: "/assets/video-1920.webp",
    imgAlt: "Filmstill einer DIMEDIAL Filmproduktion – cineastisches Gegenlicht",
    sections: [
      {
        h: "Ein guter Film erklärt nicht. Er zeigt.",
        p: [
          "Seit 2000 machen wir Film- und Videoproduktion aus Herford – Filme, die Marken und Menschen sichtbar machen, von der Idee bis zum fertigen Film aus einer Hand: Konzept, Dreh, Schnitt, Farbe, Sound.",
          "Geschichten mit Haltung – für RTL, ZDF, Mastercard oder den Eurovision Song Contest.",
        ],
      },
      {
        h: "Imagefilm, Werbefilm, Recruitingfilm.",
        p: [
          "Imagefilm für deine Website, Werbefilm für die Kampagne, Recruitingfilm für neue Leute – Format und Bildsprache passend zu deinem Ziel. Plus kurze Social-Formate, die geteilt werden.",
        ],
        list: ["Imagefilm", "Werbefilm", "Recruitingfilm", "Contentproduktion", "Eventfilm"],
      },
      {
        h: "Drohnenaufnahmen & FPV.",
        p: [
          "Vom Landschaftsschwenk bis zum fliegenden Kamerazug durch deinen Betrieb – mit Genehmigungen, Versicherung und Erfahrung aus hunderten Flügen.",
        ],
      },
    ],
    keywords: ["Filmproduktion Herford", "Videoproduktion Herford", "Imagefilm", "Werbefilm", "Recruitingfilm", "Contentproduktion", "Drohnenaufnahmen", "FPV"],
    faq: [
      ["Produziert ihr auch außerhalb von Herford?", "Ja. Wir produzieren aus Herford für ganz Ostwestfalen-Lippe, NRW und deutschlandweit."],
      ["Welche Filmarten produziert ihr?", "Imagefilm, Werbefilm, Recruitingfilm, Contentproduktion und Eventfilm – Format und Bildsprache passend zu deinem Ziel."],
      ["Bietet ihr Drohnenaufnahmen an?", "Ja, inklusive FPV – mit Genehmigungen, Versicherung und Erfahrung aus hunderten Flügen."],
      ["Übernehmt ihr das komplette Projekt?", "Ja, von der Idee bis zum fertigen Film aus einer Hand: Konzept, Dreh, Schnitt, Farbe und Sound."],
    ],
  },
  audio: {
    number: "02",
    label: "Audio",
    h1a: "Audio & Sound",
    h1b: "bei DIMEDIAL",
    title: "Audio-Produktion für Film & Marken | DIMEDIAL × VOICE.NRW",
    description: "Audio-Produktion bei DIMEDIAL: Vertonung und Sounddesign für Film und Marken. Sprachaufnahmen, Podcasts und Telefonansagen entstehen im eigenen Tonstudio VOICE.NRW in Herford.",
    img: "/assets/audio-1920.webp",
    imgAlt: "Audio-Produktion bei DIMEDIAL – Tonstudio VOICE.NRW Herford",
    sections: [
      {
        h: "Sound ist die halbe Geschichte.",
        p: [
          "Ein Film wirkt erst mit dem richtigen Sound. Vertonung, Sounddesign und Mischung entstehen bei uns im Haus – Bild und Ton aus einer Hand.",
          "Für Sprache und Stimmen arbeiten wir in unserem Tonstudio VOICE.NRW – gleicher Standort, gleiches Team.",
        ],
      },
      {
        h: "Unser Tonstudio: VOICE.NRW.",
        p: [
          "Hörproben, Telefonansagen ab 59 € und über 50 Profisprecher in 15 Sprachen findest du auf VOICE.NRW – persönlich produziert, Lieferung in 24 Stunden.",
        ],
        link: { href: "https://voicenrw.de/", label: "Professionelles Tonstudio in Herford – VOICE.NRW" },
      },
      {
        h: "Podcasts & Radiowerbung.",
        p: [
          "Podcast-Produktion, Radiospots und Voice-over produzieren wir mit VOICE.NRW – dort, wo die Kompetenz sitzt: im Tonstudio.",
        ],
        link: { href: "https://voicenrw.de/podcast", label: "Podcast-Produktion bei VOICE.NRW" },
      },
    ],
    keywords: ["Audio-Produktion", "Vertonung", "Sounddesign", "Voice Over", "Podcast", "Tonstudio VOICE.NRW"],
  },
  agentur: {
    number: "03",
    label: "Agentur",
    h1a: "Kreativagentur für Web,",
    h1b: "Grafik, Text und Kampagnen",
    title: "Werbeagentur & Grafikdesign Herford – Marketing & Web | DIMEDIAL",
    description: "Werbeagentur & Marketing-Agentur aus Herford – inhabergeführt seit 2000, persönlich statt Agentur-Blabla. Webdesign, Grafikdesign, Text, Kampagnen, Social Media und Google aus einer Hand. Für inhabergeführte Betriebe in Herford und ganz OWL.",
    img: "/assets/agentur-b-1280.webp",
    imgAlt: "Kreativarbeit der Agentur DIMEDIAL in Herford",
    sections: [
      {
        h: "Werbeagentur & Marketing-Agentur Herford.",
        p: [
          "Du suchst eine Werbeagentur oder Marketing-Agentur in Herford? DIMEDIAL ist inhabergeführt und seit dem Jahr 2000 hier zuhause – Konzept, Grafik, Text, Web, Social Media und Google aus einer Hand. Persönlich statt Agentur-Blabla, mit festen Preisen statt Stundensatz-Überraschungen.",
        ],
        list: ["Marketing & Kampagnen", "Webdesign", "Grafik & Corporate Design", "Social Media", "Google-Profil"],
        link: { href: "/social", label: "Social Media mit Robbie ansehen", internal: true },
      },
      {
        h: "Ideen bekommen bei uns eine Form.",
        p: [
          "Vom Logo über die Website bis zur Kampagne – Markenkommunikation, die zusammenhängt. Ein Team, eine Handschrift, statt zehn Dienstleister für zehn Gewerke.",
        ],
      },
      {
        h: "Webdesign aus Herford.",
        p: [
          "Websites, die gut aussehen und gefunden werden: schnell, suchmaschinenfreundlich, auf jedem Gerät. Diese hier? Selbst gebaut.",
        ],
      },
      {
        h: "Grafikdesign, Text & Kampagne.",
        p: [
          "Grafikdesign aus Herford für Print und Digital, Texte, die man zu Ende liest, Kampagnen von der Plakatwand bis zum Social-Format – gedacht aus der Sicht deiner Zielgruppe.",
        ],
        list: ["Webdesign", "Grafikdesign", "Text", "Kampagnen", "Markenkommunikation"],
      },
      {
        h: "Eigene digitale Produkte.",
        p: [
          "Mit NEWZER haben wir unsere eigene Frühwarnzentrale für öffentliche Informationen gebaut – Medienmonitoring und News-Alerts, entwickelt in Herford.",
        ],
        link: { href: "/newzer", label: "NEWZER – unser eigenes Produkt", internal: true },
      },
    ],
    keywords: ["Werbeagentur Herford", "Marketing-Agentur Herford", "Kreativagentur Herford", "Webdesign Herford", "Grafikdesign", "Text", "Kampagnen", "Markenkommunikation", "Social Media Herford"],
    faq: [
      ["Was macht ihr als Agentur?", "Konzept, Grafikdesign, Text, Web, Social Media und dein Google-Profil – aus einer Hand, mit einer Handschrift."],
      ["Arbeitet ihr mit Stundensätzen?", "Nein. Statt Stundensätzen gibt es feste Monatsprogramme für Web, Social, Google und mehr – planbar und ohne Überraschung."],
      ["Für wen arbeitet ihr?", "Für inhabergeführte Betriebe in Herford und ganz OWL – seit dem Jahr 2000, mit festem Ansprechpartner."],
      ["Macht ihr auch Webdesign?", "Ja. Websites, die gut aussehen und gefunden werden – schnell, suchmaschinenfreundlich und auf jedem Gerät."],
    ],
  },
  filmproduktion: {
    label: "Filmproduktion",
    h1a: "Filmproduktion in",
    h1b: "Herford & OWL",
    title: "Filmproduktion Herford & OWL | DIMEDIAL Studio",
    description: "Filmproduktion aus Herford für ganz Ostwestfalen-Lippe: Imagefilm, Werbefilm, Unternehmensfilm, Drohnenaufnahmen und Social-Media-Video. DIMEDIAL – seit 2000.",
    img: "/assets/video-b-1600.webp",
    imgAlt: "Drohnenaufnahme – Filmproduktion DIMEDIAL Herford",
    sections: [
      {
        h: "Von Herford in die Welt.",
        p: [
          "Seit 2000 produzieren wir Filme für Marken, Sender und Unternehmen – von der regionalen Kampagne bis zum Eurovision Song Contest. Als Filmproduktion in Herford arbeiten wir für Kunden in ganz Ostwestfalen-Lippe, NRW und deutschlandweit.",
        ],
      },
      {
        h: "Imagefilm, Werbefilm, Unternehmensfilm.",
        p: [
          "Filme, die zu deiner Marke passen – von der Idee bis zur Ausspielung. Kein Baukasten, jedes Projekt bekommt seine eigene Bildsprache.",
        ],
        list: ["Imagefilm", "Werbefilm", "Unternehmensfilm", "Recruitingfilm", "Social Media Video"],
      },
      {
        h: "Drohnenaufnahmen & FPV.",
        p: [
          "Vom Landschaftsschwenk bis zum Kamerazug durch deinen Betrieb – inklusive Genehmigungen und Erfahrung aus hunderten Flügen.",
        ],
      },
    ],
    keywords: ["Filmproduktion Herford", "Filmproduktion OWL", "Videoproduktion Herford", "Imagefilm", "Unternehmensfilm", "Social Media Video"],
  },
  webdesign: {
    label: "Webdesign",
    h1a: "Webdesign aus",
    h1b: "Herford",
    title: "Webdesign Herford | Websites von DIMEDIAL Studio",
    description: "Webdesign aus Herford: schnelle, suchmaschinenfreundliche Websites mit Design, Text und Technik aus einer Hand. DIMEDIAL Studio – Kreativagentur in OWL.",
    img: "/assets/agentur-b-1280.webp",
    imgAlt: "Webdesign und Konzeption bei DIMEDIAL in Herford",
    sections: [
      {
        h: "Websites, die gefunden werden.",
        p: [
          "Gut aussehen und gefunden werden – schnell, suchmaschinenfreundlich, auf jedem Gerät. Diese Website hier? Selbst gebaut.",
        ],
      },
      {
        h: "Design, Text und Technik aus einer Hand.",
        p: [
          "Layout, Text, Bilder und Programmierung aus einem Team – deine Website wirkt wie aus einem Guss.",
        ],
        list: ["Webdesign", "Webentwicklung", "Relaunch", "SEO-Basis", "Text"],
      },
      {
        h: "Schnell. Sicher. Datenschutzfreundlich.",
        p: [
          "Lokale Schriften, keine Tracker, optimierte Bilder – schnell und datenschutzfreundlich, ohne dass es nach Verzicht aussieht.",
        ],
      },
    ],
    keywords: ["Webdesign Herford", "Website erstellen lassen", "Responsive Design", "Relaunch", "Kreativagentur Herford"],
  },
};

const SERVICE_SCHEMA_NAME = {
  video: "Filmproduktion",
  audio: "Audio-Produktion & Tonstudio",
  agentur: "Kreativagentur & Markenkommunikation",
  filmproduktion: "Filmproduktion",
  webdesign: "Webdesign",
};

const ServicePage = ({ service }) => {
  const s = SERVICES[service];
  const camp = CAMPAIGN[service] || CAMPAIGN.agentur;
  const pagePath = service === "filmproduktion" ? "filmproduktion-herford" : service === "webdesign" ? "webdesign-herford" : service;
  const jsonLd = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "Start", item: "https://dimedial.studio/" },
          { "@type": "ListItem", position: 2, name: s.label, item: `https://dimedial.studio/${pagePath}` },
        ],
      },
      {
        "@type": "Service",
        name: `${SERVICE_SCHEMA_NAME[service] || s.label} – DIMEDIAL Herford`,
        description: s.description,
        url: `https://dimedial.studio/${pagePath}`,
        provider: { "@id": "https://dimedial.studio/#organization" },
        areaServed: ["Herford", "Ostwestfalen-Lippe", "Deutschland"],
      },
    ],
  };

  return (
    <>
      <Seo title={s.title} description={s.description} path={`/${pagePath}`} jsonLd={jsonLd} />

      <header className="relative overflow-hidden" data-testid={`${service}-hero`}>
        <div className="mx-auto max-w-[1300px] px-6 md:px-10 pt-28 md:pt-36 grid lg:grid-cols-[1.05fr_1fr] gap-10 items-end">
          <div className="relative z-10 pb-12 md:pb-20">
            <nav aria-label="Breadcrumb" className="mb-6"><ol className="flex items-center gap-3 text-xs tracking-[0.2em] uppercase text-[#737373]"><li><Link to="/" data-testid={`${service}-breadcrumb-home`} className="hover:text-[#111] transition-colors">Start</Link></li><li aria-hidden="true">/</li><li aria-current="page" className="text-[#111]">{s.label}</li></ol></nav>
            <p className="text-[11px] uppercase tracking-[0.3em] text-[#111] mb-6 inline-block border-b-[3px] border-[#FDD105] pb-1" data-testid={`${service}-campaign-label`}>{camp.label}</p>
            <h1 className="font-display font-semibold uppercase leading-[0.92] tracking-[-0.02em] text-[#111] text-[14vw] sm:text-7xl md:text-8xl xl:text-[6.5rem]" data-testid={`${service}-h1`}>
              {camp.h1.map((line, i) => <span key={line} className="block">{i === camp.h1.length - 1 ? <span className="bg-[#FDD105] px-2 box-decoration-clone">{line}</span> : line}</span>)}
            </h1>
            <p className="mt-6 text-base md:text-lg text-[#4A4A4A] max-w-md leading-relaxed" data-testid={`${service}-campaign-sub`}>{camp.sub}</p>
            <ul className="mt-6 flex flex-wrap gap-2" data-testid={`${service}-campaign-pills`}>{camp.pills.map((t) => <li key={t} className="inline-flex items-center gap-2 rounded-full border-2 border-[#111] px-3.5 py-1.5 text-xs md:text-sm font-semibold text-[#111]"><span className="inline-block h-2 w-2 rounded-full bg-[#FDD105]" style={{ boxShadow: "0 0 0 2px #111" }} />{t}</li>)}</ul>
            <TrustLine className="mt-6" />
            <div className="mt-8">{camp.cta.href ? <a href={camp.cta.href} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 rounded-full bg-[#FDD105] border-2 border-[#111] px-6 py-3.5 text-sm font-semibold text-[#111] hover:bg-[#111] hover:!text-[#FDD105] transition-colors" data-testid={`${service}-campaign-cta`}>{camp.cta.label} <ArrowRight size={16} /></a> : <Link to={camp.cta.to} className="inline-flex items-center gap-2 rounded-full bg-[#FDD105] border-2 border-[#111] px-6 py-3.5 text-sm font-semibold text-[#111] hover:bg-[#111] hover:!text-[#FDD105] transition-colors" data-testid={`${service}-campaign-cta`}>{camp.cta.label} <ArrowRight size={16} /></Link>}</div>
          </div>
          <div className="relative">
            <Note className="absolute right-0 top-2 text-right z-10 max-w-[360px]">{s.h1a}<br />{s.h1b}</Note>
            <img src={camp.animal} alt={camp.alt} className="w-full max-w-[420px] lg:max-w-[540px] mx-auto block select-none pt-20 md:pt-28" style={{ mixBlendMode: "multiply" }} loading="eager" data-testid={`${service}-campaign-animal`} />
          </div>
        </div>
      </header>
      {(service === "video" || service === "filmproduktion") && <ShowcaseReel />}

      <div className="shell py-20 md:py-28">
        <div className={`grid md:grid-cols-2 gap-5 md:gap-6 ${s.sections.length === 3 ? "xl:grid-cols-3" : ""}`} data-testid={`${service}-sections`}>
          {s.sections.map((sec, i) => (
            <FadeUp key={sec.h} delay={i * 0.08}>
              <section className="h-full rounded-3xl border border-black/10 bg-white p-6 md:p-8 flex flex-col" data-testid={`${service}-section-${i}`}>
                <p className="text-[11px] font-bold uppercase tracking-[0.25em] text-[#111]/50">0{i + 1}</p>
                <h2 className="mt-3 font-display font-semibold uppercase tracking-tight text-2xl md:text-3xl text-paper leading-tight">{sec.h}</h2>
                <p className="mt-4 text-sm md:text-base leading-relaxed text-grey">{sec.p.join(" ")}</p>
                {sec.list && <ul className="mt-5 flex flex-wrap gap-2">{sec.list.map((item) => <li key={item} className="rounded-full border border-black/15 px-3 py-1 text-xs font-semibold text-paper">{item}</li>)}</ul>}
                {sec.link && (sec.link.internal
                  ? <Link to={sec.link.href} data-testid={`${service}-section-link-${i}`} className="link-arrow mt-auto pt-6">{sec.link.label}<ArrowRight size={18} className="arrow" /></Link>
                  : <a href={sec.link.href} target="_blank" rel="noopener noreferrer" data-testid={`${service}-section-link-${i}`} className="link-arrow mt-auto pt-6">{sec.link.label}<ArrowRight size={18} className="arrow" /></a>)}
              </section>
            </FadeUp>
          ))}
        </div>
      </div>

      {service === "agentur" && (
        <>
          <PersoenlichBlock testId="agentur-persoenlich"
            headline="Werbeagentur & Marketing-Agentur in Herford?"
            text="Du suchst eine Werbeagentur oder Marketing-Agentur in Herford? Bei DIMEDIAL bekommst du beides aus einer Hand – aber ohne Agentur-Blabla und ohne Stundensatz-Überraschungen. Wir sind inhabergeführt und seit dem Jahr 2000 hier zuhause: Konzept, Grafik, Text, Web, Social Media und dein Google-Profil, betreut von Menschen, die deinen Betrieb kennen."
            punkte={[
              { Icon: Clock, t: "Seit 2000 in Herford", s: "Über zwei Jahrzehnte Marken-, Film- und Web-Erfahrung – vom Mittelständler bis zum TV-Projekt." },
              { Icon: Handshake, t: "Fester Ansprechpartner", s: "Kein wechselndes Team, keine Hotline – du redest mit denselben Menschen aus Herford." },
              { Icon: MapPin, t: "Für Betriebe in OWL", s: "Herford, Bielefeld, Gütersloh, Paderborn, Minden – und deutschlandweit, wenn du magst." },
            ]} />
          <ProgrammeUndTransfer testId="agentur-programme" einleitung="Statt Stundensätzen gibt es feste Monatsprogramme für Web, Social, Google und mehr – planbar und ohne Überraschung. Große Dateien verschickst du zusätzlich jederzeit kostenlos per Link." />
          <BranchenLinksOwl testId="agentur-branchen-owl" />
        </>
      )}

      {!["video", "filmproduktion", "audio"].includes(service) && <ReferenceGallery category={REF_CATEGORY[service]} />}
      {["web", "website", "webseiten", "webdesign"].includes(service) && <div className="pb-16"><IndividualBox dark={false} /></div>}
      {s.faq && <Fragen testId={`${service}-fragen`} items={s.faq} />}
      <ContactSection />
    </>
  );
};

export default ServicePage;
